import React from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Button, Spinner } from '@chakra-ui/react';
import { FaYoutube } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import {
  EducationProfile,
  type ProfileSurface,
} from '../../../../app/_shared/educationProfile';
import { AvatarPhotoModal } from '../../../../app/components/AvatarPicker';
import { PublicProfileUrlField } from '../../../../app/components/PublicProfileUrlField';
import {
  FormControlCheckBoxGroup,
  FormControlInput,
  FormControlSelect,
  FormControlTextArea,
} from '../../../../app/components/FormControls';
import { TIMEZONE_OPTIONS } from '../../../../helpers/timezone.helper';
import { CategoryTreeField } from '../../../category';
import { ViewProfileButton } from '../../../public';
import { PictureByGender } from '../../../user/ui/components/PictureByGender';
import { NATIVE_LANGUAGE_CODES } from '../../application/types';
import { useLazyVerifyMentorSlugQuery } from '../../infrastructure/api/mentor.api';
import { MyLessonsPanel } from '../../../lesson';
import { MentorProfileSummary } from '../components/MentorProfileSummary';
import {
  EXPERIENCE_YEAR_OPTIONS,
  MENTOR_PROFILE_STEP_SECTION_IDS,
  mentorAvatarList,
  useMentorProfileForm,
} from '../hooks/useMentorProfileForm';

const FIELD_WIDTH = '100%' as const;
const FIELD_HEIGHT = 40;

export const MentorProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const surface: Extract<ProfileSurface, 'edit' | 'pre-edit'> =
    pathname.includes('/pre-edit') ? 'pre-edit' : 'edit';
  const [verifySlug] = useLazyVerifyMentorSlugQuery();
  const {
    form,
    mentor,
    isLoading,
    isError,
    isSaving,
    canSubmit,
    canReset,
    stepsState,
    activeStepId,
    setActiveStepId,
    scrollToStep,
    onContinue,
    onCancel,
    displayName,
    timezoneLabel,
    avatar,
    isEditingPhoto,
    startPhotoEdit,
    confirmPhoto,
    onAvatarChange,
    pictureSrc,
    gender,
    categories,
    isCategoriesLoading,
    lifecycle,
    isStatusChanging,
    onPublish,
    onPause,
    onResume,
  } = useMentorProfileForm();

  const picture = (
    <>
      <button
        type="button"
        className="profile-wrapper__picture-avatar profile-wrapper__picture-avatar--editable"
        onClick={startPhotoEdit}
        aria-label={t('mentor-profile.edit_photo')}
      >
        <PictureByGender picture={pictureSrc} gender={gender} />
      </button>
      <AvatarPhotoModal
        isOpen={isEditingPhoto}
        onClose={confirmPhoto}
        title="mentor-profile.edit_photo"
        value={avatar}
        avatarList={mentorAvatarList}
        onApply={onAvatarChange}
      />
    </>
  );

  return (
    <EducationProfile
      isLoading={isLoading}
      isError={isError || !mentor}
      errorMessage={t('mentor-profile.load-error')}
      summary={
        <MentorProfileSummary
          surface={surface}
          displayName={displayName}
          timezoneLabel={timezoneLabel}
          percent={stepsState.percent}
          steps={stepsState.steps}
          activeStepId={activeStepId}
          onStepSelect={scrollToStep}
          onContinue={onContinue}
          picture={picture}
        />
      }
      skuLabel={t('mentor-profile.sku')}
      sku={mentor?.sku ?? ''}
      statusLabel={t('mentor-profile.status')}
      status={
        <span
          className={`education-profile-page__status-badge education-profile-page__status-badge--${lifecycle}`}
        >
          {t(`mentor-profile.status_${lifecycle}`)}
        </span>
      }
      statusNote={
        lifecycle === 'paused' ? (
          <p className="education-profile-page__status-note">
            {t('mentor-profile.paused_hint')}
          </p>
        ) : null
      }
      headerActions={
        <>
          {lifecycle === 'draft' ? (
            <button
              type="button"
              className="education-profile-page__preview-btn"
              disabled={isSaving || isStatusChanging}
              onClick={onPublish}
            >
              {t('mentor-profile.publish')}
            </button>
          ) : null}
          <ViewProfileButton
            role="mentor"
            slug={mentor?.slug ?? ''}
            label={t('mentor-profile.view_profile')}
            disabled={lifecycle !== 'published' || !mentor}
          />
        </>
      }
      publicProfile={
        mentor ? (
          <PublicProfileUrlField
            publicPath={mentor.publicPath}
            slug={mentor.slug}
            canEdit={mentor.profile?.updatedAt == null}
            onVerify={async (slug) => verifySlug(slug).unwrap()}
          />
        ) : null
      }
      onSubmit={form.handleSubmit}
      onCancel={onCancel}
      cancelDisabled={isSaving || isStatusChanging || !canReset}
      isSaving={isSaving}
      submitDisabled={!canSubmit || isStatusChanging}
      extraActions={
        <>
          {lifecycle === 'published' ? (
            <Button
              type="button"
              variant="outline"
              isLoading={isStatusChanging}
              isDisabled={isSaving}
              onClick={onPause}
            >
              {t('mentor-profile.pause')}
            </Button>
          ) : null}
          {lifecycle === 'paused' ? (
            <Button
              type="button"
              variant="outline"
              isLoading={isStatusChanging}
              isDisabled={isSaving}
              onClick={onResume}
            >
              {t('mentor-profile.resume')}
            </Button>
          ) : null}
        </>
      }
    >
      <section
        id={MENTOR_PROFILE_STEP_SECTION_IDS.basic}
        className="form-section form-section--basic"
      >
        <h2 className="form-section__title">
          {t('mentor-profile.steps.basic')}
        </h2>
        <div className="form-section__grid form-section__grid--2">
          <FormControlInput
            name="displayName"
            form={form}
            label={t('mentor-profile.display_name')}
            isRequired
            isValidate
            width={FIELD_WIDTH}
            height={FIELD_HEIGHT}
            className="form-field"
            onFocus={() => setActiveStepId('basic')}
          />
          <FormControlInput
            name="headline"
            form={form}
            label={t('mentor-profile.headline')}
            placeholder={t('mentor-profile.headline_placeholder')}
            isValidate
            width={FIELD_WIDTH}
            height={FIELD_HEIGHT}
            className="form-field"
            onFocus={() => setActiveStepId('basic')}
          />
        </div>
        <FormControlTextArea
          name="description"
          form={form}
          label={t('mentor-profile.about')}
          isValidate
          rows={4}
          height={120}
          width={FIELD_WIDTH}
          className="form-field"
          onFocus={() => setActiveStepId('basic')}
        />
      </section>

      <section
        id={MENTOR_PROFILE_STEP_SECTION_IDS.experience}
        className="form-section form-section--experience"
      >
        <h2 className="form-section__title">
          {t('mentor-profile.steps.experience')}
        </h2>
        <FormControlTextArea
          name="goals"
          form={form}
          label={t('mentor-profile.goals')}
          isValidate
          rows={3}
          height={96}
          width={FIELD_WIDTH}
          className="form-field"
          onFocus={() => setActiveStepId('experience')}
        />
        <div className="form-section__grid form-section__grid--2">
          <FormControlSelect
            name="experienceYears"
            form={form}
            label="mentor-profile.experience_years"
            placeholder="mentor-profile.experience_years_placeholder"
            options={EXPERIENCE_YEAR_OPTIONS}
            className="form-field"
            style={{ width: FIELD_WIDTH }}
            onChange={(value) => {
              setActiveStepId('experience');
              form.setFieldValue(
                'experienceYears',
                value == null ? '' : String(value),
              );
            }}
          />
        </div>
        <p className="form-section__placeholder">
          {t('mentor-profile.section_coming_soon')}
        </p>
      </section>

      <section
        id={MENTOR_PROFILE_STEP_SECTION_IDS.skills}
        className="form-section form-section--skills"
      >
        <h2 className="form-section__title">
          {t('mentor-profile.steps.skills')}
        </h2>
        {isCategoriesLoading ? (
          <Spinner size="sm" />
        ) : categories.length === 0 ? (
          <p className="form-section__placeholder">
            {t('mentor-profile.categories_empty')}
          </p>
        ) : (
          <CategoryTreeField
            categories={categories}
            value={form.values.categoryIds}
            onChange={(next) => {
              setActiveStepId('skills');
              form.setFieldValue('categoryIds', next);
              form.setFieldTouched('categoryIds', true);
            }}
          />
        )}
      </section>

      <section
        id={MENTOR_PROFILE_STEP_SECTION_IDS.languages}
        className="form-section form-section--languages"
      >
        <h2 className="form-section__title">
          {t('mentor-profile.steps.languages')}
        </h2>
        <FormControlCheckBoxGroup
          name="nativeLanguages"
          form={form}
          label={t('mentor-profile.native_languages')}
          className="form-field"
          onFocus={() => setActiveStepId('languages')}
          options={NATIVE_LANGUAGE_CODES.map((code) => ({
            value: code,
            label: t(`mentor-profile.lang.${code}`),
          }))}
        />
      </section>

      <section
        id={MENTOR_PROFILE_STEP_SECTION_IDS.availability}
        className="form-section form-section--availability"
      >
        <h2 className="form-section__title">{t('lesson.my_lessons')}</h2>
        <MyLessonsPanel variant="profile" />
      </section>

      <section className="form-section form-section--media">
        <h2 className="form-section__title">
          {t('mentor-profile.attachments')}
        </h2>
        <div className="form-section__grid form-section__grid--2">
          <FormControlInput
            name="introVideoUrl"
            form={form}
            label={t('mentor-profile.intro_video')}
            isValidate
            width={FIELD_WIDTH}
            height={FIELD_HEIGHT}
            className="form-field"
            leftIcon={
              <Box color="gray.500" fontSize="16px">
                <FaYoutube />
              </Box>
            }
          />
          <FormControlSelect
            name="timezone"
            form={form}
            label="mentor-profile.timezone"
            placeholder="mentor-profile.timezone_placeholder"
            options={TIMEZONE_OPTIONS}
            className="form-field"
            style={{ width: FIELD_WIDTH }}
            onChange={(value) => {
              form.setFieldValue('timezone', value == null ? '' : String(value));
            }}
          />
        </div>
      </section>
    </EducationProfile>
  );
};

export default MentorProfilePage;
